import {
  NavigationGuard,
  NavigationGuardNext,
  Route,
  RouteConfig,
} from "vue-router";
import store, { StoreData } from "@/store";
import { tap } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { Store } from "vuex";

// participant
const ParticipantTemplate = () =>
  import("@/templates/participant/participant.template.vue");
const ParticipantIndex = () =>
  import("@/components/participant/index/participant-index.vue");
const ParticipantVote = () =>
  import("@/components/participant/vote/participant-vote.vue");
const ParticipantFeedback = () =>
  import("@/components/participant/feedback/participant-feedback.vue");
const CreateFeedback = () =>
  import("@/components/participant/feedback/create-feedback.vue");
const ParticipantFeedbackDetail = () =>
  import("@/components/participant/feedback/participant-feedback-detail.vue");
const CreateProposal = () =>
  import("@/components/participant/proposal/create-proposal.vue");
const ProposalApproval = () =>
  import("@/components/participant/proposal/proposal-approval.vue");
const EntryProposalFirst = () =>
  import("@/components/participant/proposal/entry-proposal-first.vue");
const EntryProposalSecond = () =>
  import("@/components/participant/proposal/entry-proposal-second.vue");
const ViewProposalFirst = () =>
  import("@/components/participant/proposal/view-proposal-first.vue");
const ViewProposalSecond = () =>
  import("@/components/participant/proposal/view-proposal-second.vue");

const MonthlyEvaluation = () =>
  import("@/components/participant/monthly-evaluation/index.vue");
const ParticipantFaq = () => import("@/components/participant/faq/index.vue");
const ParticipantPedoman = () =>
  import("@/components/participant/pedoman/index.vue");

const JuryIndex = () => import("@/components/jury/index/jury-index.vue");
const JuryProposal = () =>
  import("@/components/jury/proposal/jury-proposal.vue");
const JuryScoring = () => import("@/components/jury/scoring/scoring.vue");

const participantMiddleware: NavigationGuard = async (
  _to: Route,
  _from: Route,
  next: NavigationGuardNext<Vue>
) => {
  const year = (store as Store<StoreData>).state.Common.currentYear;
  // Guarantee next() is called exactly once. Navigation must never hang even if a
  // dispatch rejects, an observable errors/never emits — otherwise the menu freezes
  // until a manual refresh (reported by users on prod/testing).
  let settled = false;
  const gate = () => {
    if (settled) return;
    settled = true;
    if (
      store.getters["AuthStore/isParticipant"] ||
      store.getters["AuthStore/isCommitee"] ||
      store.getters["AuthStore/isJuryRole"]
    ) {
      next();
    } else {
      next("/");
    }
  };
  Promise.all([
    store.dispatch("AuthStore/getUser"),
    store.dispatch("PeriodManagementStore/getPeriodManagementsByYear", {
      year,
    }),
  ])
    .then((res) => {
      combineLatest(res)
        .pipe(tap(gate))
        .subscribe({ error: gate, complete: gate });
    })
    .catch(gate);
  setTimeout(gate, 4000);
};

export default {
  path: "/participant",
  component: ParticipantTemplate,
  beforeEnter: participantMiddleware,
  children: [
    {
      path: "/",
      name: "Home",
      component: ParticipantIndex,
    },
    {
      path: "monthly-evaluation/:id",
      name: "Monthly Evaluation",
      component: MonthlyEvaluation,
    },
    {
      path: "proposal/create",
      name: "Create Proposal",
      component: CreateProposal,
    },
    {
      path: "proposal/:id",
      name: "Proposal",
      component: ProposalApproval,
    },
    {
      path: "proposal/1/:id/entry",
      name: "Entry Proposal Phase 1",
      component: EntryProposalFirst,
    },
    {
      path: "proposal/2/:id/entry",
      name: "Entry Proposal Phase 2",
      component: EntryProposalSecond,
    },
    {
      path: "proposal/1/:id/view",
      name: "View Proposal Phase 1",
      component: ViewProposalFirst,
    },
    {
      path: "proposal/2/:id/view",
      name: "View Proposal Phase 2",
      component: ViewProposalSecond,
    },
    {
      path: "vote",
      name: "Voting",
      component: ParticipantVote,
    },
    {
      path: "feedback",
      name: "List Feedback",
      component: ParticipantFeedback,
    },
    {
      path: "feedback/create",
      name: "Create Feedback",
      component: CreateFeedback,
    },
    {
      path: "feedback/:id",
      component: ParticipantFeedbackDetail,
    },
    {
      path: "faq",
      component: ParticipantFaq,
    },
    {
      path: "guidelines",
      component: ParticipantPedoman,
    },
    {
      path: "open-clinic",
      component: () =>
        import("@/components/participant/open-clinic/index.vue"),
    },
    {
      path: "finalreport",
      component: () =>
        import("@/components/participant/final-report/index.vue"),
    },
    {
      path: "monev",
      component: () =>
        import("@/components/participant/final-report/index-view.vue"),
    },
    {
      path: "monev/:id/view",
      component: () =>
        import("@/components/participant/monthly-evaluation/index-view.vue"),
    },
    {
      path: "finalreport/approval",
      component: () =>
        import("@/components/participant/final-report/index-approval.vue"),
    },
    {
      path: "finalreport/:id",
      component: () =>
        import("@/components/participant/final-report/final-report.vue"),
    },
    {
      path: "mypreviousproposal",
      component: () =>
        import("@/components/participant/my-previous-proposal/index.vue"),
    },
    {
      path: "term-and-condition",
      component: () =>
        import("@/components/participant/commitment-agreement/index.vue"),
    },
    {
      path: "term-and-condition/:id",
      component: () =>
        import("@/components/participant/commitment-agreement/view-detail.vue"),
    },
    {
      path: "scoring",
      component: JuryIndex,
    },
    {
      path: "scoring/proposal/:id/:juryDetailId",
      component: JuryProposal,
    },
    {
      path: "scoring/score/:phase/:user",
      name: "ParticipantScore",
      component: JuryScoring,
    },
  ],
} as RouteConfig;
